import {
  FiFileText,
  FiImage,
  FiFile,
  FiList,
  FiGrid,
  FiDownload
} from 'react-icons/fi';

import {
  AiFillFilePdf,
  AiFillFileWord
} from 'react-icons/ai';

import { useCallback, useEffect, useRef, useState } from 'react';
import { AxiosInstance } from 'axios';
import { FolderService } from '../../../services/FolderService';
import { useApi } from '../../../services/hooks/apiRedux';
import { useTranslation } from 'react-i18next';
import { errorMessageAdapter } from '../../../adapters';
import { errorToast } from '../../../utils/toasts';
import { useAuth } from '../../../store/auth';
import { Documents } from '../../../services/Interfaces/IFolderService';

type FileItem = {
  id: number;
  name: string;
  uploadedAt: string;
  signed: boolean;
  status_signed?: string
  url: string;
  signature_data?: {
    status: string
  }
  alias?: string;
};

type FileItemVersion = {
  id: number;
  name: string;
  uploadedAt: string;
  signed: boolean;
  status_signed?: string;
  url: string;
  issueDate: string;
};

type FileItemGroup = {
  alias: string;
  versions: FileItemVersion[];
};

type Props = {
  folderId: string;
  companySlug: string | number | undefined;
  onDrop?: (files: File[]) => void;
};

// const messageSigned = {
//   'pending': ''
// }

export const FileViewer = ({ folderId, companySlug, onDrop }: Props) => {
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [groupedFiles, setGroupedFiles] = useState<FileItemGroup[]>([]);

  const { t, i18n } = useTranslation();
  const api: AxiosInstance = useApi();
  const { token } = useAuth();

  // const fetchFiles = async () => {
  //   try {
  //     const response = await FolderService.getCompanyDocumentsInFolder(
  //       api,
  //       companySlug,
  //       folderId,
  //       {
  //         headers: {
  //           'Content-Language': i18n.language.toLowerCase(),
  //           Authorization: `Bearer ${token}`
  //         }
  //       }
  //     );

  //     const items: FileItem[] = response.documents.map((doc: Documents) => ({
  //       id: doc.id,
  //       name: doc.name,
  //       uploadedAt: doc.created_at,
  //       signed: doc.signatures.length > 0 ? true : false,
  //       // signed: doc.signature_data?.status == 'not_required' ? false : true,
  //       // status_signed: doc.signature_data?.status,
  //       url: `https://offshore2go-documents.s3.us-east-2.amazonaws.com/${doc.s3path}`
  //     }));

  //     setFiles(items);
  //   } catch (e: any) {
  //     errorToast(errorMessageAdapter(e));
  //   }
  // };

  const lastFetchedKeyRef = useRef<string>();

  const fetchFiles = useCallback(
    async (force = false) => {
      const cacheKey = `${companySlug}_${folderId}_${i18n.language}`;

      if (!companySlug || !folderId) {
        return;
      }

      if (!force && lastFetchedKeyRef.current === cacheKey) {
        return;
      }

      lastFetchedKeyRef.current = cacheKey;

      try {
        const response = await FolderService.getCompanyDocumentsInFolder(
          api,
          companySlug,
          folderId,
          {
            headers: {
              'Content-Language': i18n.language.toLowerCase(),
              Authorization: `Bearer ${token}`
            }
          }
        );

        const documents: Documents[] = response.documents;

        const grouped: Record<string, FileItemVersion[]> = {};
        const ungrouped: FileItem[] = [];

        documents.forEach(doc => {
          if (doc.template?.alias) {
            const alias = t(`certificatesDocuments.${doc.template.alias}`) || 'UNKNOWN';
            if (!grouped[alias]) grouped[alias] = [];

            grouped[alias].push({
              id: doc.id,
              name: doc.name,
              uploadedAt: doc.created_at,
              signed: doc.signatures.length > 0,
              url: `https://offshore2go-documents.s3.us-east-2.amazonaws.com/${doc.s3path}`,
              issueDate: doc.issue_date
            });
          } else {
            ungrouped.push({
              id: doc.id,
              name: doc.name,
              uploadedAt: doc.created_at,
              signed: doc.signatures.length > 0,
              url: `https://offshore2go-documents.s3.us-east-2.amazonaws.com/${doc.s3path}`
            });
          }
        });

        const fileGroups: FileItemGroup[] = Object.entries(grouped).map(([alias, versions]) => {
          const sorted = versions.sort((a, b) => new Date(a.issueDate).getTime() - new Date(b.issueDate).getTime());
          return { alias, versions: sorted };
        });

        setGroupedFiles(fileGroups);
        setFiles(ungrouped);
      } catch (e: any) {
        errorToast(errorMessageAdapter(e));
      }
    },
    [api, companySlug, folderId, i18n.language, t, token]
  );

  useEffect(() => {
    void fetchFiles();
  }, [fetchFiles]);

  const handleDrop = (uploaded: File[]) => {
    const newItems: FileItem[] = uploaded.map((file, i) => ({
      id: Date.now() + i,
      name: file.name,
      uploadedAt: new Date().toISOString(),
      signed: false,
      url: '#'
    }));

    setFiles(prev => [...prev, ...newItems]);
    onDrop?.(uploaded);
    lastFetchedKeyRef.current = undefined;
    void fetchFiles(true);
  };

  const handleDownload = async (file: FileItem) => {
    try {
      const response = await fetch(file.url);

      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      errorToast('Erro ao tentar fazer o download do arquivo.');
    }
  };

  const iconMap: Record<string, JSX.Element> = {
    pdf: <AiFillFilePdf className="w-6 h-6 text-red-500" />,
    doc: <AiFillFileWord className="w-6 h-6 text-blue-500" />,
    docx: <AiFillFileWord className="w-6 h-6 text-blue-500" />,
    jpg: <FiImage className="w-6 h-6 text-green-500" />,
    jpeg: <FiImage className="w-6 h-6 text-green-500" />,
    png: <FiImage className="w-6 h-6 text-green-500" />,
    webp: <FiImage className="w-6 h-6 text-green-500" />,
    txt: <FiFileText className="w-6 h-6 text-gray-500" />
  };

  const getFileIconByExtension = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase() ?? '';
    return iconMap[ext] || <FiFile className="w-6 h-6 text-gray-400" />;
  };

  return (
    <div className="space-y-4">
      { groupedFiles.length == 0 && onDrop && (
        <div
          onClick={() => document.getElementById('file-upload')?.click()}
          onDrop={(e) => {
            e.preventDefault();
            if (e.dataTransfer.files) {
              const fileArray = Array.from(e.dataTransfer.files);
              handleDrop(fileArray);
            }
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={(e) => e.preventDefault()}
          className="border border-dashed border-gray-400 rounded-md p-4 text-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
        >
          <input
            id="file-upload"
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) {
                const fileArray = Array.from(e.target.files);
                handleDrop(fileArray);
              }
            }}
          />
          <p className="text-gray-500">{t('titles.ttlCLickOrDropDocuments')}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {groupedFiles.map(group => (
          <div key={group.alias} className="bg-white rounded-lg shadow p-4 mb-4 rounded-md border-2 border-dashed border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50 transition-colors">
            <h3 className="text-lg font-semibold mb-2">{group.alias}</h3>

            <div className="space-y-2">
              {group.versions.map((version, index) => (
                <div
                  key={version.id}
                  className="flex flex-col gap-2 border rounded px-3 py-2 bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="text-sm break-words">{version.name} (v{index + 1})</p>
                    <p className="text-xs text-gray-500">
                      {`${t('labels.lblIssueDateLess') || 'Issue Date'}: ${version.issueDate}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded ${version.signed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}
                    >
                      {version.signed ? t('titles.ttlSigned') || 'Signed' : t('others.othPending') || 'Pending'}
                    </span>
                    <button onClick={() => handleDownload(version)} className="items-center text-sm text-blue-600 hover:underline">
                      <FiDownload className="w-4 h-4 mr-1" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {files.map(file => (
          <div key={file.id} className="border rounded-lg p-4 shadow-sm bg-white flex flex-col gap-2 w-full">
            <div className="flex items-center gap-3">
              {getFileIconByExtension(file.name)}
              <span className="font-medium text-sm truncate">{file.name}</span>
            </div>
            <div className="flex justify-between items-center text-xs text-gray-500">
              <span>{t('labels.lblIssueDateLess')}: {new Date(file.uploadedAt).toLocaleDateString()}</span>
              <span className={`px-2 py-0.5 rounded-full text-white text-xs ${file.signed ? 'bg-green-600' : 'bg-yellow-500'}`}>
                {file.signed ? t('titles.ttlSigned') : t('others.othPending')}
              </span>
            </div>

            <button
              onClick={() => handleDownload(file)}
              className="mt-2 inline-flex items-center text-sm text-blue-600 hover:underline"
            >
              <FiDownload className="w-4 h-4 mr-1" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
